import {
  Button,
  Center,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerOverlay,
  SimpleGrid,
  Text
} from '@chakra-ui/react'
import { FormProvider, UseFormReturn } from 'react-hook-form'
import { useTranslation } from 'react-i18next';
import { FilterField } from 'shared/types/filters';
import useGetColors from '../../hooks/useGetColors';
import useStateFromQueryParams from '../../hooks/useStateFromQueryParams';
import Field from '../form/Field';
import { useMemo } from 'react';
import ShowColumnsForm from './ShowColumnsForm';
import { setCookies } from 'shared/utils/devUtils';
import { ColumnDef } from '@tanstack/react-table';
import { TableNameE } from 'shared/api/types/common';

interface FiltersDrawerProps {
  isOpen: boolean;
  values: UnknownRecord,
  fields: FilterField[],
  onClose: () => void;
  handleFilter: (values: UnknownRecord) => void;
  handleSortColumns: (values: UnknownRecord) => void;
  methods: UseFormReturn<UnknownRecord, FixMeLater, undefined>;
  showColumnsMethods: UseFormReturn<UnknownRecord, FixMeLater, undefined>;
  columns: ColumnDef<ArrayElement<UnknownRecord>>[];
  tableName: TableNameE,
}

const FiltersDrawer = ({
  isOpen,
  fields,
  onClose,
  handleFilter,
  handleSortColumns,
  methods,
  showColumnsMethods,
  columns,
  tableName,
}: FiltersDrawerProps) => {
  const { t } = useTranslation();
  const { blue500, blue800, white } = useGetColors();
  const { setSearchParams } = useStateFromQueryParams();

  const onSubmit = (data: UnknownRecord) => {
    handleFilter(data);
    setSearchParams(data);
    onClose();
  };

  const handleSaveColumns = (data: UnknownRecord) => {
    handleSortColumns(data);
    setCookies(`columns/${tableName}`, data);
    onClose();
  }

  const showColumnFields = useMemo((): FilterField[] => (
    columns.filter(column => column.id !== 'actions')
      .filter((column) => Boolean(column.header))
      .map((column) => ({
        defaultValue: true,
        label: (column.header as string),
        name: column.id,
        type: 'checkbox'
      }))
  ), [fields])

  return (
    <Drawer
      isOpen={isOpen}
      placement="right"
      onClose={onClose}
      size="md"
    >
      <DrawerOverlay />
      <DrawerContent>
        <DrawerCloseButton />
        <DrawerBody display="flex" flexDirection="column" alignItems="center">
          <ShowColumnsForm
            fields={showColumnFields}
            methods={showColumnsMethods}
            handleSaveColumns={handleSaveColumns}
          />
          {!!fields.length && (
            <FormProvider {...methods}>
              <form onSubmit={methods.handleSubmit(onSubmit)} style={{ width: "100%" }}>
                <Text marginTop={5} fontSize="20px" py="16px" fontWeight="600" mr="auto">
                  {t('general.filters')}
                </Text>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={5} marginBottom={10} width="100%" position="relative" zIndex={1}>
                  {fields.map(field => <Field key={field.name} {...field} />)}
                </SimpleGrid>
                <Center>
                  <Button type="submit" bgColor={blue500} color={white} width="70%" _hover={{ background: blue800 }}>
                    {t('buttons.apply')}
                  </Button>
                </Center>
              </form>
            </FormProvider>
          )}
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  )
}

export default FiltersDrawer;
